import Grid from "@material-ui/core/Grid";
import React, { useEffect, useRef, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { Box, FormControlLabel, Checkbox, CardContent, Card, Typography, createStyles, makeStyles, CircularProgress } from "@material-ui/core";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Email from "@material-ui/icons/Email";
import SchoolIcon from "@material-ui/icons/School";
import Lock from "@material-ui/icons/Lock";
import { ControlledFilledInputIndex, LoadingButton } from "@onpreo/components";
import userService from "../../src/store/services/user.service";
import { useDispatch } from "react-redux";
import { enqueueSnackbar } from "@onpreo/slices";
import { createNotification } from "@onpreo/slices";
import { updateIntercom } from "next-intercom";
import { regexps } from "@onpreo/components/src/regex";
import { Business, Phone } from "@material-ui/icons";
import { PROTOCOL, BROKER_APP_URL, RECAPTCHA_SITEKEY, NODE_ENV } from "../../src/utils/secrets";
import ReCAPTCHA from "react-google-recaptcha";
import { sendLead, vid } from "../../src/utils/onetrack/onetrack-sdk";
import { checkOfferEmail } from "../../src/utils/subscription-limits/offers";

const useStyles = makeStyles(theme =>
    createStyles({
        link: {
            fontSize: 14
        },
        spacer: {
            padding: theme.spacing(0, 2)
        },
        checkboxSpacer: {
            padding: theme.spacing(2, 4)
        },
        linkSpacer: {
            padding: theme.spacing(4, 2, 1, 2)
        },
        sideliner: {
            backgroundColor: "#F7FAFC",
            width: 600,
            minHeight: "100vh"
        },
        avatarWrapper: {
            width: 300,
            height: 300,
            borderRadius: "50%",
            margin: theme.spacing(2),
            background: theme.palette.grey[300]
        },
        text: {
            color: "#8898AA",
            marginLeft: 10,
            marginBottom: 0,
            fontSize: 12
        },
        icon: {
            height: 15,
            width: 15
        }
    })
);

const signupSchema = yup.object().shape({
    name: yup.string().min(1, "Name zu kurz").max(50).required("Eintrag kann nicht leer gelassen werden"),
    email: yup.string().matches(regexps.emailRFC, "Bitte geben Sie eine gültige E-Mail Adresse ein").required("Eintrag kann nicht leer gelassen werden"),
    company: yup.string().min(2, "Name zu kurz").max(50).required("Eintrag kann nicht leer gelassen werden"),
    phone: yup.string().max(50).required("Eintrag kann nicht leer gelassen werden"),
    password: yup
        .string()
        .min(8, "Das Passwort muss mindestens 8 Zeichen lang sein")
        .max(24, "Das Passwort darf nicht länger als 24 Zeichen lang sein")
        .matches(
            /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[\W_]){8,}/,
            "Muss 8 Zeichen enthalten, ein Großbuchstabe, ein Kleinbuchstabe, eine Zahl und ein Sonderzeichen"
        )
        .required("Eintrag kann nicht leer gelassen werden"),
    hasReadAgreement: yup.bool().oneOf([true], "Bitte akzeptieren Sie die Allgemeinen Geschäftsbedingungen")
});

interface SignupData {
    name: string;
    email: string;
    company: string;
    phone: string;
    password: string;
    hasReadAgreement: boolean;
}

interface UserTracking {
    referrer?: string;
    via?: string;
    plan?: string;
    one_id?: string;
    one_vid?: string;
    rawParams?: string;
    onboardingVariant?: string;
}

export default function SignUp() {
    const classes = useStyles();
    const {
        control,
        handleSubmit,
        formState: { errors },
        watch,
        setError
    } = useForm({
        mode: "onChange",
        defaultValues: { name: "", email: "", company: "", phone: "", password: "", hasReadAgreement: false },
        resolver: yupResolver(signupSchema)
    });
    const [isSignupStarted, setIsSignupStarted] = useState(false);
    const [isReCaptchaUsed, setIsReCaptchaUsed] = useState(false);
    const dispatch = useDispatch();
    const watchAgreement = watch("hasReadAgreement");
    const captchaRef = useRef(null);
    const [query, setQuery] = useState(undefined);
    const [hasTrial, setHasTrial] = useState(false);
    const [referral, setReferral] = useState(null);
    const [oneVid, setOneVid] = useState(undefined);

    const handleReCaptcha = data => {
        console.log(data);
        setIsReCaptchaUsed(!!data);
    };

    useEffect(() => {
        const urlSearchParams = new URLSearchParams(window.location.search);
        const params = Object.fromEntries(urlSearchParams?.entries());
        setQuery(params);

        if (params.of === "KEN" || params.of === "SKIP") setHasTrial(false);
        else setHasTrial(true);

        if (NODE_ENV === "production")
            // the rewardful functions are being loaded in a script tag and are not available as imports
            // @ts-ignore
            rewardful("ready", function () {
                // @ts-ignore
                setReferral(Rewardful?.referral);
            });
    }, []);

    useEffect(() => {
        (async () => {
            const oV = await vid();
            if (oV) setOneVid(oV);
        })();
    }, []);

    const handleSignup: SubmitHandler<SignupData> = async data => {
        setIsSignupStarted(true);

        if (data.phone) {
            try {
                await userService.validatePhone({ phone: data.phone });
            } catch (e) {
                setError("phone", { type: "validation", message: "Eingabe ungültig!" });
                return;
            }
        }

        const urlSearchParams = new URLSearchParams(window.location.search);
        const params = Object.fromEntries(urlSearchParams?.entries());

        if (data.email && (!!params?.offer || !!params?.of)) {
            const validMail = checkOfferEmail(data.email, params?.offer ?? params?.of);
            if (!validMail) {
                setError("email", { type: "validation", message: "Ungültige Adresse. Geben Sie die Adresse des Kooperationspartners an." });
                setIsSignupStarted(false);
                return;
            }
        }

        let userData: SignupData & { tracking?: UserTracking; token?: string; offer?: string } = { ...data };
        if (Object.keys(params).length > 0) userData.tracking = { ...userData.tracking, rawParams: window.location.search };
        if (!!params?.referrer) userData.tracking = { ...userData.tracking, referrer: params.referrer };
        if (!!params?.via) userData.tracking = { ...userData.tracking, via: referral };
        if (!!params?.plan) userData.tracking = { ...userData.tracking, plan: params.plan };
        if (!!params?.one_id) userData.tracking = { ...userData.tracking, one_id: params.one_id };
        if (!!oneVid) userData.tracking = { ...userData.tracking, one_vid: oneVid };
        if (!!params?.onboardingVariant) userData.tracking = { ...userData.tracking, onboardingVariant: params.onboardingVariant };

        userData = { ...userData, token: captchaRef.current.getValue(), offer: params?.offer ?? params?.of };

        if (typeof window !== "undefined" && window.location.hostname === "agent.onpreo.app")
            await userService
                .signupZap({
                    lastName: data.name.split(" ").slice(1, data.name.split(" ").length + 1)[0],
                    firstName: data.name.split(" ").slice(0, 1)[0],
                    email: data.email,
                    company: data.company,
                    phone: data.phone,
                    params: params
                })
                .catch(e => console.log("trouble connecting zapier"));

        await userService
            .createUser(userData)
            .then(async () => {
                // await signIn("credentials", { email: data.email, password: data.password });
                try {
                    sendLead({
                        pageInfo: { url: `${BROKER_APP_URL}/start`, title: "Trial Signup" }
                    });
                } catch (err) {
                    console.error("[Create User] - Failed to send to one-track");
                    console.log(err);
                }
                window.parent.location.href = encodeURI(
                    `${PROTOCOL}${BROKER_APP_URL}/authenticate?email=${data.email}&password=${data.password}&callbackPath=${"profile"}`
                );
                setIsSignupStarted(false);
            })
            .catch(e => {
                dispatch(enqueueSnackbar(createNotification("Benutzer mit der angegebenen E-Mail-Adresse existiert bereits", "error")));
                setIsSignupStarted(false);
            })
            .finally(() => setIsSignupStarted(false));
    };

    useEffect(() => {
        updateIntercom("update", {
            hide_default_launcher: true
        });
    });

    return (
        <Grid
            container
            item
            justifyContent="center"
            alignItems={"center"}
            style={{ margin: 0, display: "flex", width: "100%", background: "#FFFFFF", height: "100vh", paddingRight: 0, paddingLeft: 0 }}
        >
            <Card style={{ padding: 0, maxWidth: 454, boxShadow: "none", background: "#FFFFFF" }}>
                {/* <CardContent style={{ marginLeft: 15, paddingBottom: 5 }}>
                    {!hasTrial && (
                        <Grid container justifyContent={"flex-start"} alignItems="center">
                            <VerifiedUserIcon color="primary" className={classes.icon} />
                            <Typography variant="body2" align="center">
                                Sofort starten ohne zu warten
                            </Typography>
                        </Grid>
                    )}
                    {hasTrial && (
                        <Grid container justifyContent={"flex-start"} alignItems="center" style={{ paddingTop: 10 }}>
                            <VerifiedUserIcon color="primary" className={classes.icon} />
                            <Typography variant="body2" align="center" className={classes.text}>
                                Testen Sie onpreo zunächst {(query?.offer ?? query?.of) === "SP30" ? 30 : 14} Tage kostenlos
                            </Typography>
                        </Grid>
                    )}
                    {(query?.offer ?? query?.of === "KEN") && (
                        <Grid container justifyContent={"flex-start"} alignItems="center" style={{ paddingTop: 10 }}>
                            <VerifiedUserIcon color="primary" className={classes.icon} />
                            <Typography variant="body2" align="center" className={classes.text}>
                                KENSINGTON Event Sonderkonditionen nutzen
                            </Typography>
                        </Grid>
                    )}
                </CardContent> */}
                <form>
                    <div className={classes.spacer}>
                        <Grid container justifyContent={"center"} alignItems={"center"} direction={"column"}>
                            <Grid item style={{ width: "100%" }}>
                                <ControlledFilledInputIndex
                                    control={control}
                                    errors={!!errors.name}
                                    placeholder={"Name*"}
                                    name={"name"}
                                    required={true}
                                    icon={<SchoolIcon />}
                                />
                            </Grid>
                            <Grid item style={{ width: "100%" }}>
                                <Typography variant="inherit" color="error">
                                    {errors.name?.message}
                                </Typography>
                            </Grid>
                        </Grid>
                    </div>
                    <div className={classes.spacer}>
                        <Grid container justifyContent={"center"} alignItems={"center"} direction={"column"}>
                            <Grid item style={{ width: "100%" }}>
                                <ControlledFilledInputIndex
                                    control={control}
                                    errors={!!errors.email}
                                    placeholder={"E-Mail*"}
                                    name={"email"}
                                    required={true}
                                    icon={<Email />}
                                />
                            </Grid>
                            <Grid item style={{ width: "100%" }}>
                                <Typography variant="inherit" color="error">
                                    {errors.email?.message}
                                </Typography>
                            </Grid>
                        </Grid>
                    </div>
                    <div className={classes.spacer}>
                        <Grid container justifyContent={"center"} alignItems={"center"} direction={"column"}>
                            <Grid item style={{ width: "100%" }}>
                                <ControlledFilledInputIndex
                                    control={control}
                                    errors={!!errors.company}
                                    placeholder={"Firma*"}
                                    name={"company"}
                                    required={true}
                                    icon={<Business />}
                                />
                            </Grid>
                            <Grid item style={{ width: "100%" }}>
                                <Typography variant="inherit" color="error">
                                    {errors.company?.message}
                                </Typography>
                            </Grid>
                        </Grid>
                    </div>
                    <div className={classes.spacer}>
                        <Grid container justifyContent={"center"} alignItems={"center"} direction={"column"}>
                            <Grid item style={{ width: "100%" }}>
                                <ControlledFilledInputIndex
                                    control={control}
                                    errors={!!errors.phone}
                                    placeholder={"Telefon*"}
                                    name={"phone"}
                                    required={true}
                                    icon={<Phone />}
                                />
                            </Grid>
                            <Grid item style={{ width: "100%" }}>
                                <Typography variant="inherit" color="error">
                                    {errors.phone?.message}
                                </Typography>
                            </Grid>
                        </Grid>
                    </div>
                    <div className={classes.spacer}>
                        <Grid container justifyContent={"center"} alignItems={"center"} direction={"column"}>
                            <Grid item style={{ width: "100%" }}>
                                <ControlledFilledInputIndex
                                    control={control}
                                    errors={!!errors.password}
                                    type={"password"}
                                    placeholder={"Passwort"}
                                    name={"password"}
                                    required={true}
                                    icon={<Lock />}
                                />
                            </Grid>
                            <Grid item style={{ width: "100%" }}>
                                <Typography variant="inherit" color="error">
                                    {errors.password?.message}
                                </Typography>
                            </Grid>
                        </Grid>
                    </div>
                    <div className={classes.checkboxSpacer}>
                        <Grid container justifyContent={"flex-start"}>
                            <Controller
                                control={control}
                                name="hasReadAgreement"
                                render={({ field: { value, onChange } }) => (
                                    <FormControlLabel
                                        control={<Checkbox color={"primary"} checked={value} onChange={onChange} />}
                                        label={
                                            <Typography style={{ fontSize: 15 }} color={errors.hasReadAgreement ? "error" : "inherit"}>
                                                Ich habe die{" "}
                                                <a
                                                    target={"_blank"}
                                                    rel="noopener noreferrer"
                                                    href={"https://www.onpreo.com/agbs"}
                                                    className={classes.link}
                                                >
                                                    Nutzungsbedingungen{" "}
                                                </a>
                                                und die
                                                <a
                                                    target={"_blank"}
                                                    rel="noopener noreferrer"
                                                    href={"https://www.onpreo.com/privacy-policy"}
                                                    className={classes.link}
                                                >
                                                    {" "}
                                                    Datenschutzbestimmungen
                                                </a>{" "}
                                                gelesen und stimme diesen zu
                                            </Typography>
                                        }
                                    />
                                )}
                            />
                        </Grid>
                    </div>
                    <Grid item style={{ width: "100%" }}>
                        <Typography variant="inherit" color="error">
                            {errors.hasReadAgreement ? "(" + errors.hasReadAgreement.message + ")" : ""}
                        </Typography>
                    </Grid>{" "}
                    <Grid container justifyContent="center" item style={{ width: "100%" }}>
                        <Box mt={0} mb={3}>
                            <ReCAPTCHA
                                sitekey={RECAPTCHA_SITEKEY}
                                ref={captchaRef}
                                onChange={handleReCaptcha}
                                onExpired={() => setIsReCaptchaUsed(false)}
                            />
                        </Box>
                    </Grid>
                    <div className={classes.spacer}>
                        <Grid container justifyContent={"center"}>
                            <LoadingButton
                                className={!watchAgreement || isSignupStarted ? undefined : "app-signup-button-click"}
                                id={!watchAgreement || isSignupStarted ? undefined : "app-signup-button-click"}
                                disabled={!watchAgreement || isSignupStarted || !isReCaptchaUsed}
                                type="submit"
                                variant="contained"
                                color="primary"
                                onClick={handleSubmit(handleSignup)}
                            >
                                {isSignupStarted && <CircularProgress color="primary" size={"1em"} />}
                                {!isSignupStarted && hasTrial ? "Account kostenlos erstellen" : "Jetzt Account erstellen"}
                            </LoadingButton>
                        </Grid>
                        <Grid container justifyContent={"center"} style={{ paddingTop: 10 }}>
                            <a target={"_blank"} rel="noopener noreferrer" href={"https://agent.onpreo.app/login"} className={classes.link}>
                                Ich habe bereits einen Account
                            </a>
                        </Grid>
                    </div>
                </form>
            </Card>
        </Grid>
    );
}
